import React, { Component, createRef } from 'react'
import { Alert, Box, Button, Grid, IconButton, MenuItem, Select, SelectChangeEvent, Snackbar, Typography } from '@mui/material';
import { decodeJwt, rolenum, UserRole } from '../ApiCommonService';
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate } from 'react-router-dom';
import { fetchCSRUsersByBroker, getbroker } from '../../ApiSevices/UserMasterApis';
import { getLobsByBrokerId, CreateQCJobPayload, CreateQCJob, UploadQCDocument, CreateQuoteActivity } from '../../ApiSevices/QuoteCompareAPI';
interface IProps {
    displaysidebar: boolean;
    basePath?: string;
    navigate?: (path: string) => void;
}

interface UploadedFile {
    name: any;
    file: File;
    docType: string;
}

interface Broker {
    Broker_ID: number;
    vchBrokerName: string;
}

interface CSR {
    ID: number,
    vchEmail: string;
}

interface IState {
    isLoading: boolean;
    lobCodes: { Lob_Name: string; Lob_Code: string, LobId: number }[];
    errors: {
        lineOfCoverage: string;
        checklist: string;
        currentTermPolicy: string;
        priorTermPolicy: string;
        duplicateFiles: string;
        sourceDoc: string;
    },
    selectedCoverage: string;
    Role_Id: any;
    uploadedFiles: { [key: string]: UploadedFile[] };
    snackbarOpen: boolean,
    snackbarMessage: string,
    snackbarSeverity: "error",
    brokers: Broker[];
    csrs: CSR[];
    csrId: number;
    csr: string;
    broker: string;
    isMsUser: boolean;
    uploadErrors: {
        [fieldName: string]: string;
    };
}

const validateFileUpload = (fieldName: string, file: File, existingFiles: UploadedFile[]) => {
    const pdfOnlyFields = ["Current Term Policy", "Prior Term Policy", "Current Endorsement", "Prior Endorsement", "Policy OR Application"]
    const PEW_FilesOnly = ["Proposal", "Quotes"]                  // Pdf, Excel & Word Only
    const PW_FilesOnly = ["Carrier Quote", "Binder"]    // Pdf, Word Only
    const PE_FilesOnly = ["Acord Application", "Schedule"]  // Pdf, Excel Only

    let allowedFormats: string[] | undefined;
    const extentionNameMap: Record<string, string> = {
        pdf: "PDF",
        doc: "Word",
        docx: "Word",
        xlsx: 'Excel',
        xls: 'Excel',
        json: "JSON"
    }

    if (fieldName === "Acord Application") {
        // Allow Word, PDF, and Excel for Acord Application
        allowedFormats = ["pdf", "xlsx", "xls", "doc", "docx",];
    } else if (PEW_FilesOnly?.includes(fieldName)) {
        allowedFormats = ["pdf", "doc", "docx", "xlsx", "xls"];
    } else if (pdfOnlyFields?.includes(fieldName)) {
        allowedFormats = ["pdf"];
    } else if (PW_FilesOnly?.includes(fieldName)) {
        allowedFormats = ["pdf", "doc", "docx"];
    } else if (PE_FilesOnly?.includes(fieldName)) {
        allowedFormats = ["pdf", "xlsx", "xls"];
    }

    const fileExtension = file?.name?.split(".").pop()?.toLowerCase();
    if (!fileExtension || !allowedFormats?.includes(fileExtension)) {
        const namesSet = new Set<string>();

        for (const exe of allowedFormats ?? []) {
            const name = extentionNameMap[exe];
            if (name) namesSet.add(name)
        }

        const typeNames = Array.from(namesSet);
        const last = typeNames.pop();
        const readable = typeNames.length
            ? `${typeNames.join(", ")} and ${last}`
            : last;

        return `Only ${readable} files are allowed`;
    }

    // Enforce single file restriction for specific fields
    const singleFileFields = ["Current Term Policy", "Prior Term Policy"];
    if (singleFileFields?.includes(fieldName) && existingFiles?.length > 0) {
        return `${fieldName} allows only a single file`;
    }

    // // Handle maximum file uploads validation
    // const maxFileValidation = ["Current Endorsement", "Prior Endorsement"];
    // if (maxFileValidation?.includes(fieldName) && existingFiles?.length >= 4) {
    //     return `${fieldName} allows maximum count 4 for upload`;
    // }
    return null;
};


class QuoteCompareUploadDocument extends Component<IProps, IState> {
    private fileInputRefs: Record<string, React.RefObject<HTMLInputElement>>;
    private brokerMap: Map<string, Broker>;
    docTypeArray: Record<string, number> = { "Current Term Policy": 1, "Prior Term Policy": 2, "Current Endorsement": 3, "Prior Endorsement": 4, "Acord Application": 5, "Carrier Quote": 6, "Proposal": 7, "Schedule": 8, "Binder": 9 }


    constructor(props: IProps) {
        super(props);
        this.state = {
            isLoading: false,
            lobCodes: [],
            errors: {
                lineOfCoverage: '',
                checklist: '',
                currentTermPolicy: '',
                priorTermPolicy: '',
                duplicateFiles: '',
                sourceDoc: ''
            },
            selectedCoverage: '',
            Role_Id: null,
            uploadedFiles: {},
            snackbarOpen: false,
            snackbarMessage: "",
            snackbarSeverity: "error",
            brokers: [],
            isMsUser: false,
            broker: "",
            csr: "",
            csrs: [],
            csrId: 0,
            uploadErrors: {},
        }
        this.fileInputRefs = {};
        this.brokerMap = new Map();
    }

    async componentDidMount() {
        try {
            const token = sessionStorage.getItem('token');
            const decodedToken = token ? decodeJwt(token) : null;
            const roleId = decodedToken ? decodedToken.RoleId : null;
            const isMSUser = rolenum.MSUser === roleId;
            this.setState({ isLoading: true, isMsUser: isMSUser }, async () => {
                await Promise.all([this.fetchLobs(0), this.fetchBrokers(isMSUser)]);
                if (token) {
                    if (decodedToken) {
                        this.setState({ Role_Id: decodedToken.RoleId });
                    } else {
                        console.warn("Token decoding failed or returned null");
                    }
                }
            });
        } catch (error) {
            console.error('Error in component rendering:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    handleCoverageChange = async (event: SelectChangeEvent<string>) => {
        const selectedCoverage = event?.target?.value;
        const { errors } = this.state;
        this.setState((prevState) => ({
            selectedCoverage: selectedCoverage,
            errors: {
                ...prevState.errors,
                lineOfCoverage: "",  // Clear only the lineOfCoverage error
            },
        }))
    };

    fetchLobs = async (brokerId: number) => {
        try {
            this.setState({ isLoading: true });
            const token = sessionStorage.getItem('token');
            const decodedToken = token ? decodeJwt(token) : null;
            const borkerIdInToken = decodedToken?.BrokerId;
            const { isMsUser } = this.state;
            if (isMsUser && !brokerId) {
                this.setState({ lobCodes: [] });
                return;
            }
            const fetchResponse = await getLobsByBrokerId(isMsUser ? brokerId : borkerIdInToken);
            if (fetchResponse && fetchResponse?.length > 0) {
                const mappedResponse = fetchResponse.map((lob: any) => ({ LobId: lob.ID, Lob_Code: lob.LobCode, Lob_Name: lob.LobDescription }));
                this.setState({ lobCodes: Array.from(new Set(mappedResponse)) });
            }
        } catch (error) {
            console.error('Error fetching lobData:', error);
        }
        finally {
            this.setState({ isLoading: false });
        }
    };

    fetchBrokers = async (isMsUser: boolean) => {
        try {
            if (!isMsUser) {
                return;
            }
            const brokers = await getbroker();
            const validBrokers = brokers.filter((b: Broker) => b?.vchBrokerName);
            validBrokers.forEach((b: Broker) => {
                this.brokerMap.set(b.vchBrokerName, b);
            });
            const sortedBrokers = validBrokers.sort((a: any, b: any) =>
                a.vchBrokerName.localeCompare(b.vchBrokerName)
            );
            this.setState({ brokers: sortedBrokers, isLoading: false });

        } catch (err) {
            console.error('Error loading brokers', err);
            this.setState({ isLoading: false, snackbarMessage: 'Error loading brokers', snackbarSeverity: 'error', snackbarOpen: true });
        }

    }

    handleDrop = (e: React.DragEvent<HTMLDivElement>, fieldName: string) => {
        e.preventDefault();
        let files = e.dataTransfer.files;
        //only single file for "Current Term Policy" and "Prior Term Policy"
        const isSingleFile = ["Current Term Policy", "Prior Term Policy"].includes(fieldName);
        if (isSingleFile && files.length > 1) {
            alert(`Only one file is allowed for the ${fieldName}.`);
            return;
        }
        const fileList = isSingleFile ? [files[0]] : Array.from(files);
        const event = {
            target: { files: fileList }
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        this.handleFileUpload(event, fieldName);
    };

    handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
        const { files } = e.target;
        const { uploadedFiles } = this.state;
        // const maxSize = 80 * 1024 * 1024; // 80MB
        if (!files) return;

        if (files && files.length > 0) {
            let newFiles: UploadedFile[] = [];

            // Get existing files for multi-file support
            const existingFiles: UploadedFile[] = (uploadedFiles?.[fieldName] ?? []) as UploadedFile[];

            if (
                fieldName === "Quotes" &&
                existingFiles.length + files.length > 5
            ) {
                this.setState({
                    snackbarOpen: true,
                    snackbarMessage: "Quotes allows a maximum of 5 files only",
                    snackbarSeverity: "error",
                });

                e.target.value = "";
                return;
            }
            if (
                fieldName === "Policy OR Application" &&
                existingFiles.length + files.length > 1
            ) {
                this.setState({
                    snackbarOpen: true,
                    snackbarMessage: "Policy OR Application allows a maximum of 1 file only",
                    snackbarSeverity: "error",
                });

                e.target.value = "";
                return;
            }
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const sameFieldFileNameCheck = existingFiles?.some(item => item?.name === file?.name);
                if (sameFieldFileNameCheck) {
                    alert(`This file has already been selected:\n ${file?.name}`);
                    e.target.value = '';
                    return false;
                }

                const errorMessage = validateFileUpload(fieldName, file, existingFiles);

                if (errorMessage) {
                    this.setState(
                        { snackbarOpen: true, snackbarMessage: errorMessage },
                        () => console.error("Snackbar triggered with message:", errorMessage)
                    );
                    e.target.value = "";
                    return;
                }

                const docType = String(this.docTypeArray[fieldName] || "");
                newFiles.push({ name: file.name, file, docType });
            }

            if (newFiles.length > 0) {
                this.setState((prevState) => {
                    const prevFiles: UploadedFile[] = (prevState.uploadedFiles?.[fieldName] ?? []) as UploadedFile[];

                    return {
                        uploadedFiles: {
                            ...prevState.uploadedFiles,
                            [fieldName]: fieldName === "Current Term Policy" || fieldName === "Prior Term Policy"
                                ? newFiles  // Replace for single-file fields
                                : [...prevFiles, ...newFiles],  // Append for multi-file fields
                        },

                        errors: {
                            ...prevState.errors,
                            currentTermPolicy: fieldName === "Current Term Policy" ? "" : prevState.errors?.currentTermPolicy,
                            priorTermPolicy: fieldName === "Prior Term Policy" ? "" : prevState.errors?.priorTermPolicy,
                            lineOfCoverage: fieldName !== "Current Term Policy" && fieldName !== "Prior Term Policy"
                                ? "" : prevState.errors?.lineOfCoverage,
                        }
                    };
                });
            }
        }

        // Reset input to allow re-upload of the same file
        const inputFile = this.fileInputRefs[fieldName]?.current;
        if (inputFile) {
            inputFile.value = "";
        }
    };

    handleFileRemove = (fieldName: string, fileIndex?: number) => {
        const { uploadedFiles } = this.state;
        const existingFiles = uploadedFiles?.[fieldName];

        if (Array.isArray(existingFiles)) {
            if (fileIndex !== undefined && fileIndex >= 0 && fileIndex < existingFiles.length) {
                // Remove specific file
                const updatedFiles = [...existingFiles];
                updatedFiles.splice(fileIndex, 1);

                this.setState({
                    uploadedFiles: {
                        ...uploadedFiles,
                        [fieldName]: updatedFiles.length ? updatedFiles : [],
                    },
                });
            } else if (fileIndex === undefined) {
                // Remove all files for the field by deleting the key
                const { [fieldName]: _, ...rest } = uploadedFiles;
                this.setState({
                    uploadedFiles: {
                        ...rest,
                    },
                });
            }
        } else {
            // Nothing to remove
            return;
        }
        if (this.fileInputRefs[fieldName]?.current) {
            this.fileInputRefs[fieldName].current!.value = "";
        }
    };

    handleCloseSnackbar = () => {
        this.setState({ snackbarOpen: false });
    };

    validateSubmit(): boolean | undefined {
        const { selectedCoverage, uploadedFiles, isMsUser, csrId, broker } = this.state;
        const quoteFiles = uploadedFiles?.["Quotes"] ?? [];
        const policyFiles = uploadedFiles?.["Policy OR Application"] ?? [];
        if (isMsUser) {
            return !(selectedCoverage !== "" && quoteFiles.length !== 0 && policyFiles.length !== 0 && csrId !== 0 && broker !== "");
        }
        else {
            return !(selectedCoverage !== "" && quoteFiles.length !== 0 && policyFiles.length !== 0);
        }
    }

    onCancel = () => {
        // const basePath = this.props?.basePath || 'quote';
        // // navigate(`/${basePath}/jobs/joblisting?statusId=0`, { replace: true });
        // window.history.replaceState({}, '', `${basePath}/jobs/joblisting?statusId=0`);
    }

    handleBrokerChange = async (event: SelectChangeEvent<string>) => {
        const broker = event.target.value;
        this.setState({ broker, csr: '', selectedCoverage: '', isLoading: true });

        const selectedBroker = this.brokerMap.get(broker);
        if (!selectedBroker) return;

        try {
            await Promise.all([this.fetchLobs(selectedBroker.Broker_ID), fetchCSRUsersByBroker(selectedBroker.Broker_ID)]).then(async ([, csrUsers]) => {
                this.setState({ csrs: csrUsers || [], csr: '', isLoading: false });
            });
        } catch (err) {
            console.error('Error fetching CSR users', err);
            this.setState({ isLoading: false, snackbarMessage: 'Error loading CSR users', snackbarSeverity: 'error', snackbarOpen: true });
        }
    };


    onCsrUserChange = (e: SelectChangeEvent) => {
        if (e?.target?.value) {
            const userData = this.state.csrs.find((f: any) => f.vchEmail === e.target.value)
            this.setState({ csr: e.target.value, csrId: userData?.ID || 0 })
        } else {
            this.setState({ csr: '', csrId: 0 })
        }
    }


    handleReset = async () => {
        this.setState({
            uploadedFiles: {},
            broker: "",
            selectedCoverage: ""
        }, () => {
            // Reset file inputs manually
            Object.keys(this.fileInputRefs)?.forEach((key) => {
                if (this.fileInputRefs[key].current) {
                    this.fileInputRefs[key].current!.value = "";
                }
            });
        });
    };

    onSubmit = async () => {
        this.setState({ isLoading: true });
        try {
            const { broker, csrId, uploadedFiles, Role_Id, selectedCoverage } = this.state;
            const { basePath } = this.props;

            const token = sessionStorage.getItem('token');
            const decodedToken = token ? decodeJwt(token) : null;

            const body: CreateQCJobPayload = {
                TenantId: broker
                    ? this.brokerMap.get(broker)?.Broker_ID || 0
                    : decodedToken?.BrokerId || 0,
                LobID: +selectedCoverage,
                JobTypeID: 2,
                JobSubTypeID: 3,
                ClientName: broker,
                ChecklistTypeID: 2,
                ReferenceUserId: csrId || 0
            };
            const res = await CreateQCJob(body);
            if (!res?.data) {
                this.setState({
                    snackbarOpen: true,
                    snackbarMessage: "Job creation failed",
                    snackbarSeverity: "error"
                });
                return;
            }

            const jobId = res.data.data.ID;
            const uploadPromises: Promise<any>[] = [];

            Object.keys(uploadedFiles).forEach((key) => {
                const files = uploadedFiles[key];
                const isPrimaryDoc = key === "Policy OR Application";

                files.forEach((fileObj: any, index: number) => {
                    uploadPromises.push(
                        UploadQCDocument(isPrimaryDoc, jobId, body.TenantId, fileObj.file)
                            .then((res) => {
                                if (res !== "File Uploaded Successfully") {
                                    throw new Error(res || "Upload failed");
                                }
                            })
                            .catch((err) => {
                                this.setState((prev) => ({
                                    uploadErrors: {
                                        ...prev.uploadErrors,
                                        [key]: "File upload failed. Please try again.",
                                    },
                                }));
                                throw err; // stop submit flow
                            })
                    );
                });
            });

            const uploadResponse = await Promise.allSettled(uploadPromises);

            if (uploadResponse.every(r => r.status === 'fulfilled')) {
                alert(`Files Uploaded Successfully for Job ID: ${res.data?.data.JobCode}`);
                await CreateQuoteActivity({ JobId: jobId, TenantId: body.TenantId });
                await this.handleReset();

                setTimeout(() => {
                    if ([UserRole?.CSR, UserRole?.BrokerAdmin].includes(Role_Id)) {
                        window.location.href = basePath + "/jobs/jobcsrlisting";
                    } else {
                        window.location.href = basePath + "/jobs/joblisting";
                    }
                }, 2000);
            }

        } catch (error) {
            console.error('Error during submission:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };


    renderFileUpload = (fieldName: string, label: string, isRequired: boolean = false) => {
        if (!this.fileInputRefs[fieldName]) {
            this.fileInputRefs[fieldName] = createRef();
        }
        const { uploadedFiles, selectedCoverage } = this.state;

        const isMultiFile = ["Current Endorsement", "Prior Endorsement", "Quotes"].includes(fieldName);
        const isPrimaryDoc = fieldName === "Policy OR Application";
        const errorMsg = this.state.uploadErrors[fieldName];
        const files: UploadedFile[] = (uploadedFiles?.[fieldName] ?? []) as UploadedFile[];
        // const isDisabled = selectedCoverage === ""; // Add your logic to determine if the field should be disabled
        const isDisabled = false;
        return (
            <fieldset style={{
                display: "flex",
                border: "1px solid #ddd", padding: "10px", borderRadius: "5px", minHeight: "70px",
                flexDirection: "column",
                gap: "10px",
            }}>
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => !isDisabled && this.handleDrop(e, fieldName)}
                    onDragLeave={(e) => e.preventDefault()}
                    style={{
                        border: '2px dashed #ccc',
                        padding: '10px',
                        borderRadius: '5px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isDisabled ? '#f9f9f9' : '#fafafa',
                    }}
                >
                    <Grid container spacing={1} alignItems="flex-start" flexWrap="wrap">
                        <Grid item lg={12} sm={6}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {label} {isRequired && <span style={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>*</span>}
                            </Typography>
                        </Grid>

                        <Grid item lg={3} sm={6}>
                            <Button
                                variant="contained"
                                component="label"
                                size="small"
                                disabled={isDisabled}
                                sx={{ fontSize: '8px', color: 'black', border: isDisabled ? '1px solid lightgray' : '1px solid black', backgroundColor: 'lightgray' }}
                            >
                                <span>Choose File</span>
                                <input
                                    type="file"
                                    multiple={isMultiFile}
                                    hidden
                                    ref={this.fileInputRefs[fieldName]}
                                    onChange={(e) => this.handleFileUpload(e, fieldName)}
                                />
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container>
                        {files.length > 0 &&
                            files.map((file: UploadedFile, index: number) => (
                                <Grid item lg={3} sm={6} md={4} xs={12} key={index} style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", }}>
                                    <span style={{ fontSize: "10px" }}>{file.name}</span>
                                    <IconButton size="small" onClick={() => this.handleFileRemove(fieldName, index)} disabled={isDisabled}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            ))
                        }
                    </Grid>
                    {errorMsg && (
                        <Typography sx={{ color: "red", fontSize: "11px", marginTop: "4px" }}>
                            {errorMsg}
                        </Typography>
                    )}
                </div>
            </fieldset >
        );
    };

    render() {
        const { isLoading, lobCodes, selectedCoverage, errors, snackbarMessage, snackbarOpen, snackbarSeverity, brokers, broker, isMsUser, csr, csrs } = this.state;
        const { displaysidebar } = this.props;
        return (
            <>
                {isLoading && (
                    <Box sx={{
                        position: 'absolute',
                        top: -60,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                    </Box>
                )}
                <div className={displaysidebar ? 'displaysidebar' : 'hidesidebar'}>
                    <Box className="qc-container overflow-y-scroll ">
                        {/* Breadcrumb */}

                        {/* LOB */}
                        <h2 style={{ fontWeight: "bold", fontSize: '15px' }}>
                            QUOTE COMPARE UPLOAD
                        </h2>
                        <Grid container marginTop={1} marginBottom={2} spacing={1}>
                            {isMsUser &&
                                <Grid item lg={4} sm={6} xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                        Select Broker <span style={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>*</span>
                                    </Typography>
                                    <Select
                                        id="coverage"
                                        value={broker}
                                        onChange={this.handleBrokerChange}
                                        displayEmpty
                                        fullWidth
                                        sx={{
                                            height: '30px',
                                            '& .MuiOutlinedInput-input': { fontSize: '12px' },
                                        }}
                                    >
                                        {brokers.map(b => (
                                            <MenuItem
                                                key={b.Broker_ID}
                                                value={b.vchBrokerName}
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {b.vchBrokerName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors?.lineOfCoverage && (
                                        <span style={{ color: 'red', fontSize: 'smaller' }}>
                                            {errors?.lineOfCoverage}
                                        </span>
                                    )}
                                </Grid>
                            }
                            {isMsUser &&
                                <Grid item lg={4} sm={6} xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                        Primary CSR <span style={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>*</span>
                                    </Typography>
                                    <Select
                                        id="coverage"
                                        value={csr}
                                        onChange={this.onCsrUserChange}
                                        displayEmpty
                                        fullWidth
                                        sx={{
                                            height: '30px',
                                            '& .MuiOutlinedInput-input': { fontSize: '12px' },
                                        }}
                                    >
                                        {csrs.map((c, i) => (
                                            <MenuItem
                                                key={i}
                                                value={c.vchEmail}
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {c.vchEmail}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors?.lineOfCoverage && (
                                        <span style={{ color: 'red', fontSize: 'smaller' }}>
                                            {errors?.lineOfCoverage}
                                        </span>
                                    )}
                                </Grid>
                            }
                            <Grid item lg={isMsUser ? 4 : 6} sm={6} xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                    Line of Coverage <span style={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}>*</span>
                                </Typography>
                                <Select
                                    id="coverage"
                                    value={selectedCoverage}
                                    onChange={this.handleCoverageChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        height: '30px',
                                        '& .MuiOutlinedInput-input': { fontSize: '12px' },
                                    }}
                                >
                                    {lobCodes?.map((item, index) => (
                                        <MenuItem key={index} value={item.LobId} sx={{ fontSize: '12px' }} onKeyDown={(e) => {
                                            if (e.key === " ") {
                                                e.preventDefault();
                                                (e.target as HTMLElement).click(); // S
                                            }
                                        }}>{item.Lob_Name}</MenuItem>
                                    ))}
                                </Select>
                                {errors?.lineOfCoverage && (
                                    <span style={{ color: 'red', fontSize: 'smaller' }}>
                                        {errors?.lineOfCoverage}
                                    </span>
                                )}
                            </Grid>

                        </Grid>

                        {/* Upload Section */}
                        <Grid container spacing={2}>
                            <Grid item lg={12} sm={12} marginTop={0.5} xs={12}>
                                {this.renderFileUpload("Policy OR Application", "Policy OR Application", true)}
                                {errors?.currentTermPolicy && (
                                    <span style={{ color: 'red', fontSize: 'smaller' }}>
                                        {errors?.currentTermPolicy}
                                    </span>
                                )}
                            </Grid>
                            <Grid item lg={12} sm={12} marginTop={0.5} xs={12}>
                                {this.renderFileUpload("Quotes", "Quotes", true)}
                                {errors?.currentTermPolicy && (
                                    <span style={{ color: 'red', fontSize: 'smaller' }}>
                                        {errors?.currentTermPolicy}
                                    </span>
                                )}
                            </Grid>
                        </Grid>
                        <div className='qc-button-row mt-1'>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={this.validateSubmit()}
                                sx={{
                                    backgroundColor: '#2a327d',
                                    fontSize: '0.75rem',
                                    height: 30,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                }}
                                onClick={this.onSubmit}
                            >
                                Submit
                            </Button>
                            <Link to={[UserRole?.CSR, UserRole?.BrokerAdmin].includes(this.state.Role_Id) ? this.props.basePath + "/jobs/jobcsrlisting" : this.props.basePath + "/jobs/joblisting"}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    // onClick={this.onCancel}
                                    sx={{
                                        backgroundColor: '#2a327d',
                                        fontSize: '0.75rem',
                                        height: 30,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Link>
                        </div>

                        {/* Buttons */}
                    </Box>


                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={2500}
                        onClose={this.handleCloseSnackbar}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <Alert severity={snackbarSeverity} onClose={this.handleCloseSnackbar}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </div>
            </>
        )
    }

}

export default QuoteCompareUploadDocument;